import React, { useState } from 'react';

import { useIntl } from '../../util/reactIntl';
import { searchSchools } from '../../util/api';

import css from './SchoolSearchField.module.css';

/**
 * A postcode-based school search and selection component.
 *
 * User enters a postcode, clicks Search, sees nearby schools, and picks one.
 * The selected school's URN and name are passed back via onSelect callback.
 *
 * @param {Object} props
 * @param {function} props.onSelect - Called with { urn, name } when a school is selected
 * @param {string} [props.initialSchoolName] - Name of already-selected school (for display)
 */
const SchoolSearchField = props => {
  const { onSelect, initialSchoolName } = props;
  const intl = useIntl();

  const [postcode, setPostcode] = useState('');
  const [schools, setSchools] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedName, setSelectedName] = useState(initialSchoolName || null);
  const [showResults, setShowResults] = useState(!initialSchoolName);

  const handleSearch = () => {
    const trimmed = postcode.trim();
    if (!trimmed) return;

    setSearching(true);
    setError(null);
    setSchools([]);
    setHasSearched(false);

    searchSchools(trimmed)
      .then(data => {
        setSchools(data.schools || []);
        setHasSearched(true);
      })
      .catch(() => {
        setError(intl.formatMessage({ id: 'SchoolSearchField.error' }));
      })
      .finally(() => {
        setSearching(false);
      });
  };

  const handleSelect = school => {
    setSelectedName(school.name);
    setShowResults(false);
    onSelect({ urn: school.urn, name: school.name });
  };

  const handleChange = () => {
    setSelectedName(null);
    setShowResults(true);
  };

  // If a school is already selected, show it with a "Change" link
  if (selectedName && !showResults) {
    return (
      <div className={css.root}>
        <p className={css.selectedName}>{selectedName}</p>
        <span className={css.changeLink} onClick={handleChange} role="button" tabIndex={0}>
          {intl.formatMessage({ id: 'SchoolSearchField.changeSchool' })}
        </span>
      </div>
    );
  }

  return (
    <div className={css.root}>
      <div className={css.searchRow}>
        <input
          className={css.postcodeInput}
          type="text"
          value={postcode}
          onChange={e => setPostcode(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
          placeholder={intl.formatMessage({ id: 'SchoolSearchField.postcodePlaceholder' })}
        />
        <button
          className={css.searchButton}
          type="button"
          onClick={handleSearch}
          disabled={searching || !postcode.trim()}
        >
          {searching
            ? intl.formatMessage({ id: 'SchoolSearchField.searching' })
            : intl.formatMessage({ id: 'SchoolSearchField.searchButton' })}
        </button>
      </div>

      {error ? <p className={css.error}>{error}</p> : null}

      {searching ? (
        <div className={css.searching}>
          {intl.formatMessage({ id: 'SchoolSearchField.searching' })}
        </div>
      ) : null}

      {!searching && hasSearched && schools.length === 0 ? (
        <div className={css.noResults}>
          {intl.formatMessage({ id: 'SchoolSearchField.noResults' })}
        </div>
      ) : null}

      {!searching && schools.length > 0 ? (
        <div className={css.resultsList}>
          {schools.map(school => (
            <div
              key={school.urn}
              className={css.resultItem}
              onClick={() => handleSelect(school)}
              role="button"
              tabIndex={0}
            >
              <p className={css.resultName}>{school.name}</p>
              <p className={css.resultMeta}>
                {[
                  school.phase,
                  school.ofstedRating ? `Ofsted: ${school.ofstedRating}` : null,
                  school.distanceMiles != null
                    ? `${school.distanceMiles.toFixed(1)} miles`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' • ')}
              </p>
              {school.address ? <p className={css.resultMeta}>{school.address}</p> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default SchoolSearchField;
